import { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SearchIcon from '@mui/icons-material/Search';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useOrganizationFolders } from '../hooks/useOrganizationFolders';
import type { OrganizationFolder, OrganizationFolderAccess } from '../types';

interface FolderShareModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  /** The organization's full member email list. */
  memberEmails: string[];
  /** Owner email — always has access and cannot be unchecked. */
  ownerEmail: string;
  /** Provide for edit mode; undefined = create mode. */
  folder?: OrganizationFolder;
  /** Receives the created/updated folder so callers can react to it (e.g. select it). */
  onSaved?: (folder?: OrganizationFolder) => void;
}

/** Derive a Title Case display name from an email's local part. */
const deriveNameFromEmail = (email: string) => {
  const local = email.split('@')[0] ?? email;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

interface TeamMemberSelectListProps {
  memberEmails: string[];
  ownerEmail: string;
  selected: Set<string>;
  onToggle: (email: string) => void;
}

const TeamMemberSelectList = ({ memberEmails, ownerEmail, selected, onToggle }: TeamMemberSelectListProps) => {
  const [search, setSearch] = useState('');

  const ownerLower = ownerEmail.toLowerCase();
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return memberEmails;
    return memberEmails.filter((email) => {
      const name = deriveNameFromEmail(email).toLowerCase();
      return email.toLowerCase().includes(q) || name.includes(q);
    });
  }, [memberEmails, search]);

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        placeholder="Search team members..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        fullWidth
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />
      <Box
        sx={{
          maxHeight: 280,
          overflowY: 'auto',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Stack divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
          {filtered.map((email) => {
            const isOwnerEmail = email.toLowerCase() === ownerLower;
            const checked = isOwnerEmail || selected.has(email);
            return (
              <Stack key={email} direction="row" alignItems="center" spacing={1.5} sx={{ px: 1.5, py: 1, minWidth: 0 }}>
                <Avatar
                  sx={{
                    bgcolor: isOwnerEmail ? 'primary.main' : 'grey.500',
                    width: 36,
                    height: 36,
                    fontSize: '0.95rem',
                  }}
                >
                  {email.charAt(0).toUpperCase()}
                </Avatar>
                <Stack sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                    {deriveNameFromEmail(email)}
                    {isOwnerEmail && (
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                        (Owner)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {email}
                  </Typography>
                </Stack>
                <Chip label="Can view" size="small" variant="outlined" />
                <Checkbox
                  checked={checked}
                  disabled={isOwnerEmail}
                  onChange={() => onToggle(email)}
                  inputProps={{ 'aria-label': `Toggle access for ${email}` }}
                />
              </Stack>
            );
          })}
        </Stack>
      </Box>
    </Stack>
  );
};

const FolderShareModal = ({
  open,
  onClose,
  organizationId,
  memberEmails,
  ownerEmail,
  folder,
  onSaved,
}: FolderShareModalProps) => {
  const isEdit = !!folder;
  const showAlert = useAlertStore((state) => state.showAlert);
  const { createFolder, updateFolder, deleteFolder, isMutating } = useOrganizationFolders(organizationId);

  const [name, setName] = useState('');
  const [access, setAccess] = useState<OrganizationFolderAccess>('team');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Hydrate the form whenever the modal opens (or the target folder changes).
  useEffect(() => {
    if (!open) return;
    setName(folder?.name ?? '');
    setAccess(folder?.access ?? 'team');
    setSelected(new Set(folder?.member_emails ?? []));
    setDeleteConfirmOpen(false);
  }, [open, folder]);

  const handleToggle = (email: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    // When restricting to specific people, send the checked emails including
    // the owner (who always has access).
    const memberEmailsPayload =
      access === 'members' ? Array.from(new Set([...Array.from(selected), ownerEmail])) : undefined;

    try {
      if (isEdit && folder) {
        const updated = await updateFolder({
          organizationId,
          folderId: folder.id,
          name: trimmed,
          access,
          memberEmails: memberEmailsPayload,
        });
        showAlert('Folder updated.', 'success');
        onSaved?.(updated);
      } else {
        const created = await createFolder({
          organizationId,
          name: trimmed,
          access,
          memberEmails: memberEmailsPayload,
        });
        showAlert('Folder created.', 'success');
        onSaved?.(created);
      }
      onClose();
    } catch (error: unknown) {
      showAlert(
        getApiErrorMessage(
          error,
          isEdit ? 'Could not update folder. Please try again.' : 'Could not create folder. Please try again.',
        ),
        'error',
      );
    }
  };

  const handleDelete = async () => {
    if (!folder) return;
    try {
      await deleteFolder({ organizationId, folderId: folder.id });
      showAlert('Folder deleted.', 'success');
      setDeleteConfirmOpen(false);
      onSaved?.();
      onClose();
    } catch (error: unknown) {
      showAlert(getApiErrorMessage(error, 'Could not delete folder. Please try again.'), 'error');
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={isMutating ? undefined : onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEdit ? 'Share folder' : 'Create folder'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose who on your team can see this folder.
          </Typography>

          <TextField
            autoFocus
            label="Folder name"
            placeholder="e.g. Onboarding"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            disabled={isMutating}
            sx={{ mb: 2 }}
          />

          <ToggleButtonGroup
            value={access}
            exclusive
            onChange={(_, value: OrganizationFolderAccess | null) => {
              if (value) setAccess(value);
            }}
            fullWidth
            size="small"
            sx={{ mb: access === 'members' ? 2 : 0 }}
          >
            <ToggleButton value="team">
              <GroupsOutlinedIcon fontSize="small" sx={{ mr: 0.75 }} />
              Entire team
            </ToggleButton>
            <ToggleButton value="members">
              <PersonOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
              Specific people
            </ToggleButton>
          </ToggleButtonGroup>

          {access === 'members' && (
            <TeamMemberSelectList
              memberEmails={memberEmails}
              ownerEmail={ownerEmail}
              selected={selected}
              onToggle={handleToggle}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: isEdit ? 'space-between' : 'flex-end', px: 3, pb: 2 }}>
          {isEdit && (
            <Button color="error" onClick={() => setDeleteConfirmOpen(true)} disabled={isMutating}>
              Delete
            </Button>
          )}
          <Stack direction="row" spacing={1}>
            <Button onClick={onClose} disabled={isMutating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!name.trim() || isMutating}
              startIcon={isMutating ? <CircularProgress size={16} /> : undefined}
            >
              {isEdit ? 'Save' : 'Create'}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => (isMutating ? null : setDeleteConfirmOpen(false))}>
        <DialogTitle>Delete folder?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{folder?.name}&quot;? The notes in it will not be deleted — they stay
            shared with the team.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={isMutating}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" disabled={isMutating} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FolderShareModal;
